import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { api, API } from "@/lib/api";
import { FileText, Upload, Trash2, Loader2, Paperclip, CheckCircle2, AlertTriangle } from "lucide-react";
import { useTranslation } from "react-i18next";

/**
 * Reusable .docx uploader tied to a module and optional target_id.
 * - module: "assistant" | "configurer" | "ontologie" | "risque" | "opportunite"
 * - target_id: string | null (for risks/opportunities)
 * - onChange(docs): called when list changes (after upload/delete)
 * - compact: boolean (smaller UI for cards / modals)
 * - title: optional header label
 */
export default function DocxUploader({
  module,
  targetId = null,
  title,
  compact = false,
  onChange,
  accept = ".docx",
}) {
  const { t } = useTranslation();
  const effectiveTitle = title === undefined ? t("uploader.attached_docs") : title;
  const [docs, setDocs] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);

  const load = async () => {
    try {
      const params = { module };
      if (targetId) params.target_id = targetId;
      const r = await api.get("/docs", { params });
      setDocs(r.data || []);
      onChange?.(r.data || []);
    } catch (e) {
      // silent
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [module, targetId]);

  const doUpload = async (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".docx")) {
      setError(t("uploader.only_docx"));
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      setError(t("uploader.too_big"));
      return;
    }
    setError(null);
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("module", module);
      if (targetId) form.append("target_id", targetId);
      await axios.post(`${API}/docs/upload`, form, {
        headers: { "Content-Type": "multipart/form-data" },
        withCredentials: true,
      });
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || t("uploader.import_error"));
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer?.files?.[0];
    if (f) doUpload(f);
  };

  const removeDoc = async (id) => {
    try {
      await api.delete(`/docs/${id}`);
      await load();
    } catch {}
  };

  const zoneClass = dragOver
    ? "border-[#378ADD] bg-[#378ADD]/5"
    : "border-white/10 hover:border-white/25";

  return (
    <div className="space-y-3" data-testid={`docx-uploader-${module}`}>
      {effectiveTitle && !compact && (
        <div className="flex items-center gap-2 text-zinc-300 text-xs uppercase tracking-wider">
          <Paperclip className="w-3.5 h-3.5 text-[#378ADD]" />
          <span>{effectiveTitle}</span>
          {docs.length > 0 && (
            <span className="text-zinc-500 normal-case tracking-normal">· {docs.length}</span>
          )}
        </div>
      )}

      <div
        className={`rounded-lg border border-dashed ${zoneClass} transition-colors ${
          compact ? "p-3" : "p-4"
        }`}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
      >
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-md bg-[#378ADD]/10 border border-[#378ADD]/30 flex items-center justify-center">
            <Upload className="w-4 h-4 text-[#378ADD]" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm text-zinc-200">
              {t("uploader.drop_hint")} <span className="text-[#378ADD]">.docx</span>
            </div>
            <div className="text-xs text-zinc-500">
              {t("uploader.or_browse")}
            </div>
          </div>
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            className="btn-ghost text-xs disabled:opacity-50"
            data-testid={`docx-upload-btn-${module}`}
          >
            {uploading ? (
              <><Loader2 className="w-3.5 h-3.5 animate-spin" /> {t("uploader.uploading")}</>
            ) : (
              <>{t("uploader.browse")}</>
            )}
          </button>
          <input
            ref={inputRef}
            type="file"
            accept={accept}
            className="hidden"
            onChange={(e) => doUpload(e.target.files?.[0])}
            data-testid={`docx-input-${module}`}
          />
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 text-xs text-[#E24B4A]">
          <AlertTriangle className="w-3.5 h-3.5" /> {error}
        </div>
      )}

      {docs.length > 0 && (
        <ul className="space-y-1.5" data-testid={`docx-list-${module}`}>
          {docs.map((d) => (
            <li
              key={d.doc_id}
              className="flex items-start gap-2 p-2 rounded-md bg-white/[0.02] border border-white/5 hover:bg-white/[0.04] transition-colors"
            >
              <FileText className="w-3.5 h-3.5 mt-1 text-[#378ADD] flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <div className="text-sm text-zinc-200 truncate">{d.filename}</div>
                  <CheckCircle2 className="w-3 h-3 text-[#1D9E75] flex-shrink-0" />
                </div>
                <div className="text-[11px] text-zinc-500">
                  {Math.ceil(d.char_count / 1000)}k {t("uploader.characters")} · {new Date(d.created_at).toLocaleDateString()}
                </div>
                {!compact && d.text_preview && (
                  <div className="text-[11px] text-zinc-400 mt-1 line-clamp-2">
                    {d.text_preview}
                  </div>
                )}
              </div>
              <button
                onClick={() => removeDoc(d.doc_id)}
                className="p-1 rounded hover:bg-white/5 text-zinc-500 hover:text-[#E24B4A] flex-shrink-0"
                title={t("uploader.delete")}
                data-testid={`docx-delete-${d.doc_id}`}
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
